import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IApi20Result, IAsmxDto, IComment } from '@valhalla/data/entities';
import { Observable, of, throwError, zip } from 'rxjs';
import { catchError, map, mapTo } from 'rxjs/operators';
import ApiVersion from '../api-versions';

import { DataHttpCommonService } from '../data-http-common.service';
import {
	CommentsDataHttpService,
	IGetCommentViewersCriteria,
	IGetCommentViewersResult,
	IMarkCommentAsQuestionCriteria,
	ICommentFavoriteCriteria,
	IGetCommentsOffsetCriteria,
	IGetCommentsOffsetResult,
	IEditCommentsCriteria,
	IGetPreviousTaskTextResult,
} from './abstract';

@Injectable()
export class CommentsDataHttpServiceImpl implements CommentsDataHttpService {
	constructor(public readonly http: HttpClient, public readonly common: DataHttpCommonService) {}

	// no comments...
	readonly colorForRemove = '#ffffff';

	readonly commentFavoriteColorDefault = '#039be5'; //mat blue active

	getCommentViewers(criteria: IGetCommentViewersCriteria): Observable<IGetCommentViewersResult> {
		const url = this.common.getEndpointUrl(`/TCWeb.asmx/GetTaskCommentRecipients`);
		const body = {
			commentID: criteria?.commentId,
			searchInArchiveIfNotFound:
				typeof criteria?.searchInArchiveIfNotFound === 'undefined' ? true : criteria?.searchInArchiveIfNotFound,
		};

		return this.http.post<IAsmxDto<any>>(url, body).pipe(
			map(result => result.d),
			map(data => this.mapGetTaskCommentRecipients(data))
		);
	}

	markCommentsAsRead(...commentIds: number[]): Observable<any> {
		const url = this.common.getEndpointUrl('/iOSClientServices/DataService.asmx/MarkCommentsAsRead');
		return this.http.post(url, { commentIds }).pipe(mapTo(true));
	}

	markCommentAsUnread(commentId: number): Observable<any> {
		const url = this.common.getEndpointUrl('/TCWeb.asmx/MarkCommentAsUnread');
		return this.http.post(url, { commentID: commentId }).pipe(mapTo(true));
	}

	markCommentAsQuestion(data: IMarkCommentAsQuestionCriteria): Observable<any> {
		if (!data || !data.commentIds || data.commentIds.length === 0) {
			return of(true);
		}

		const url = this.common.getEndpointUrl('/TCWeb.asmx/MarkCommentAsQuestion');

		const requests$ = data.commentIds.map(commentId =>
			this.http.post<IAsmxDto>(url, {
				commentID: commentId,
			})
		);

		return zip(...requests$).pipe(mapTo(true));
	}

	markCommentsAsAnswered(commentId: number): Observable<any> {
		const url = this.common.getEndpointUrl('/TCWeb.asmx/MarkCommentAsAnswered');

		return this.http
			.post(url, {
				commentText: null,
				isForAll: null,
				commentID: commentId,
			})
			.pipe(mapTo(true));
	}

	markCommentsAsNotMyQuestion(commentId: number): Observable<any> {
		const url = this.common.getEndpointUrl('/TCWeb.asmx/MarkCommentAsNotMyQuetion');

		return this.http.post(url, { commentId }).pipe(mapTo(true));
	}

	addCommentToFavorite({ commentId, color }: ICommentFavoriteCriteria): Observable<any> {
		color = !color || color === this.colorForRemove ? this.commentFavoriteColorDefault : color;

		const url = this.common.getEndpointUrl('/TCWeb.asmx/SetCommentColor');

		return this.http.post<IAsmxDto>(url, { commentId, color: '#E65100' });
	}

	removeCommentToFavorite({ commentId }: ICommentFavoriteCriteria): Observable<any> {
		const url = this.common.getEndpointUrl('/TCWeb.asmx/SetCommentColor');

		return this.http.post<IAsmxDto>(url, { commentId, color: this.colorForRemove });
	}

	getCommentsSlice(criteria: IGetCommentsOffsetCriteria): Observable<IGetCommentsOffsetResult> {
		if (criteria.searchString && !criteria.searchType) {
			criteria.searchType = 'Contains';
		}
		const url = this.common.getApiUrl('comments');
		return this.http.post<IApi20Result<IGetCommentsOffsetResult>>(url, criteria).pipe(map(result => result.data));
	}

	getComment(commentId: number): Observable<Partial<IComment>> {
		return this.getCommentsSlice({
			commentId: commentId,
			limitAfter: 0,
			limitBefore: 0,
			taskInfo: false,
		}).pipe(map(result => result.comments[0]));
	}

	delete(...ids: number[]): Observable<any> {
		if (ids.length <= 0) {
			return of();
		}
		const url = this.common.getApiUrl('comments/delete');
		return this.http
			.post<IApi20Result<any>>(url, {
				isDeleted: true,
				ids: ids,
			})
			.pipe(
				catchError(err => {
					const hasErrors = Array.isArray(err?.error?.errors);
					if (hasErrors && err.error.errors[0]?.message) {
						return throwError(new Error(err.error.errors[0].message));
					}
					return throwError(err);
				})
			);
	}

	edit(model: IEditCommentsCriteria) {
		if (!model?.commentId) {
			return throwError(new Error('commentId is not defined'));
		}
		const url = this.common.getApiUrl(`mobile/comments/${model.commentId}/edit`, ApiVersion.v12);
		return this.http.post<any>(url, { text: model.text });
	}

	getPreviousTaskText(commentId: number): Observable<IGetPreviousTaskTextResult> {
		const url = this.common.getApiUrl(`comments/previous-task-text/${commentId}`);
		return this.http.get<IGetPreviousTaskTextResult>(url);
	}

	protected mapGetTaskCommentRecipients(data: any): IGetCommentViewersResult {
		const result: IGetCommentViewersResult = {
			users: [],
		};

		if (!Array.isArray(data)) {
			return result;
		}

		data.forEach(({ First, Second }) => {
			result.users.push({
				seenAt: Second,
				user: {
					displayName: First.DisplayName,
					userName: First.DisplayName,
					userId: First.UserID,
					isOnline: First.IsOnline,
				},
			});
		});

		return result;
	}
}
